import React, { FC, useEffect, useRef, useState } from 'react';
import TwDialog from '../../components/TwDialog/TwDialog';
import { TwButton, TwButtonType } from '../../components/TwButtons/TwButtons';
import { openCreateTrip } from '../../redux/features/navigation/navigationSlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import Grid from '@mui/material/Grid';
import QuickMenuItems from '../CreateOrder/QuickMenuItems/QuickMenuItems';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import useOnScreen from '../../hooks/useOnScreen';
import TripWrapperForm from './TripWrapperForm/TripWrapperForm';
import TripOrderInfo from './TripOrderInfo/TripOrderInfo';
import TripRouteInfo from './TripRoute/TripRoute';
import DispatchInfo from '../CreateOrder/Dispatch/DispatchInfo';
import AttachmentsInfo from '../CreateOrder/Attachments/AttachmentsInfo';
import { clearLtlTripState, updateTripId } from '../../redux/features/trip/tripSlice';
import { useCreateTripMutation } from '../../redux/api/trips/tripApi';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import './CreateTrip.scss';
import TwNotifyDriver, {
  INotifyDriverWithFullSegment
} from '../../components/TwNotifyDriver/TwNotifyDriver';
import { logRocketTrack } from '../../utils/logRocketUtils';
import useAuth from '../../providers/Auth/Auth.provider';
import {
  STOP_TYPE,
  Trip,
  TripCommodities,
  TripDriver,
  TripDriverInfo,
  TripStop,
  UpsertTrip
} from '../../redux/api/trips/trips.model';
import { LOAD_TYPE } from '../../redux/api/load/load.model';
import DateUtils from '../../utils/dateUtils';
import { EntityType } from '../../redux/Global.model';
import DriverUtils from '../../utils/driver/driverUtils';
import { useUploadOrderTripDocumentMutation } from '../../redux/api/orders/ordersApi';
import TwWarningHelper from '../../components/TwWarningHelper/TwWarningHelper';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';
import _ from 'lodash';
import {
  FLOWS,
  Validation,
  ValidationsResponse
} from '../../redux/api/validationsHelper/validationsHelper.model';
import { useValidateDispatchMutation } from '../../redux/api/validationsHelper/validationsApi';
import { clearLoadState, setOrdersForConsolidation } from '../../redux/features/orders/ordersSlice';
import { PROVIDER } from '../../providers/User/User.model';
import { TwLeaveWarning } from '../../components/TwLeaveWarning/TwLeaveWarning';
import TwDriverPayConfirmationModal from '../../components/TwDriverPayCalc/TwDriverPayConfirmationModal';
import { mapCommodityListToTripCommoditiesForSave } from '../../utils/commodity/commodityUtils';

interface CreateTripProps {
  open: boolean;
  clearSelectedOrders?: () => void;
  consolidatedOrders?: { id: string; readable_id: number | string }[];
  onConsolidationSuccess?: (tripId: string) => void;
  onCreateSuccess?: (tripId: string) => void;
}

export const INTERSECTION_OPTIONS: IntersectionObserverInit = {
  threshold: 0.33
};

const CreateTrip: FC<CreateTripProps> = ({
  open,
  consolidatedOrders,
  clearSelectedOrders,
  onConsolidationSuccess,
  onCreateSuccess
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const dispatch = useAppDispatch();
  const { addNotification } = useNotifications();
  const matchesAboveLarge = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const { driver_pay_confirmation_modal, ...tripData } = useAppSelector((state) => state.trip);
  const { enableWarningHelper, enableSkipDispatchModal, enableCommoditiesDatabase } =
    useFeatureFlags();

  const formRef = useRef<HTMLFormElement>(null);
  const orderInfoRef = useRef<HTMLDivElement>(null);
  const routeInfoRef = useRef<HTMLDivElement>(null);
  const dispatchRef = useRef<HTMLDivElement>(null);
  const attachmentsInfoRef = useRef<HTMLDivElement>(null);

  const isOrderInfoVisible = useOnScreen(orderInfoRef, INTERSECTION_OPTIONS);
  const isRouteInfoVisible = useOnScreen(routeInfoRef, INTERSECTION_OPTIONS);
  const isDispatchInfoVisible = useOnScreen(dispatchRef, INTERSECTION_OPTIONS);
  const isAttachmentsVisible = useOnScreen(attachmentsInfoRef, INTERSECTION_OPTIONS);

  const [isOpenWarning, setIsOpenWarning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Notify driver component
  const [openNotify, setOpenNotify] = useState(false);
  const [driversToNotify, setDriversToNotify] = useState<INotifyDriverWithFullSegment[]>([]);
  const [tripId, setTripId] = useState(undefined);

  // dispatch warning helper
  const [validations, setValidations] = useState<Validation[]>([]);
  const [openWarningHelper, setOpenWarningHelper] = useState(false);
  const [validateDispatch] = useValidateDispatchMutation();

  const [createTrip] = useCreateTripMutation();
  const [uploadOrderTripDocument] = useUploadOrderTripDocumentMutation();

  const handleWarningClose = (): void => {
    setIsOpenWarning(false);
  };

  const handleCloseCreateTrip = (): void => {
    setIsOpenWarning(false);
    dispatch(clearLtlTripState());
    dispatch(openCreateTrip(false));
    dispatch(setOrdersForConsolidation([]));
    dispatch(clearLoadState());

    // clears selected orders state from Uncovered orders page
    clearSelectedOrders && clearSelectedOrders();
  };

  const closeNotify = (): void => {
    setOpenNotify(false);
    setDriversToNotify([]);
    handleCloseCreateTrip();
  };

  const handleFormSubmit = (): void => {
    if (formRef.current) {
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
    dispatch(clearLoadState());
  };

  const createTripAndUploadDocs = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      const tripRequest: UpsertTrip = {
        trip_commodities: [],
        instructions: tripData.instructions,
        dispatcher_id: tripData.selected_dispatcher || null,
        owner_operator_id: tripData.selected_owner_operator || null,
        order_type: LOAD_TYPE.SIMPLE,
        truck_id: tripData.selected_truck || null,
        trailer_id: tripData.selected_trailer || null,
        carrier_id: tripData.selected_carrier || null,
        sort_order: tripData.sort_order || 1,
        is_empty_miles_auto: tripData.is_empty_miles_auto,
        is_loaded_miles_auto: tripData.is_loaded_miles_auto,
        empty_mileage_source: tripData.empty_mileage_source || PROVIDER.GOOGLE,
        loaded_mileage_source: tripData.loaded_mileage_source || PROVIDER.GOOGLE,
        estimated_mileage: tripData.loaded_miles || null,
        empty_miles: tripData.empty_miles || null,
        stops: [],
        drivers: []
      };

      const cleanCommodities: TripCommodities[] = [];

      if (enableCommoditiesDatabase) {
        tripData.new_trip_commodities?.forEach((commodity) => {
          if (
            (commodity.name === '' || commodity.name !== null) &&
            !commodity.quantity &&
            !commodity.weight
          ) {
            return;
          } else {
            cleanCommodities.push(mapCommodityListToTripCommoditiesForSave(commodity));
          }
        });
      } else {
        // clean commodities, remove empty ones
        tripData.trip_commodities.forEach((commodity: TripCommodities) => {
          if (commodity.description === '' && !commodity.quantity && !commodity.weight) {
            return;
          } else {
            const comm: TripCommodities = {
              description: commodity.description,
              quantity: commodity.quantity || 0,
              weight: commodity.weight || 0,
              ui_uid: commodity.ui_uid,
              order_commodity_id: commodity.id ? commodity.id : undefined,
              packaging_unit: commodity.packaging_unit || null
            };

            cleanCommodities.push(comm);
          }
        });
      }

      if (cleanCommodities.length > 0) {
        tripRequest.trip_commodities = cleanCommodities;
      }

      // Drivers check
      const driversForTrip = tripData.selected_drivers.filter((d) => !!d.id);
      driversForTrip.length > 0
        ? (tripRequest.drivers = driversForTrip.map((d) => {
            const driver: TripDriverInfo = {
              id: d.id || '',
              is_primary: d.is_primary,
              send_driver_link: d.send_driver_link,
              trip_driver_pay_other_payment: d.trip_driver_pay_other_payment
            };

            if (d.driver_pay_override_data) {
              driver.driver_pay_override_data = {
                ...d.driver_pay_override_data,
                orders_rate_totals: undefined
              };
            }

            return driver;
          }))
        : undefined;

      // Build stops
      tripRequest.stops = tripData.provider_trip_stops.map((s) => {
        const stop: TripStop = {
          id: s.id || undefined,
          order: s.order,
          sort_order: s.order,
          start_date: s.provider_date ? DateUtils.getShortDate(s.provider_date) : '',
          start_time: s.provider_time_from
            ? DateUtils.get24HourFormat(DateUtils.getTime(s.provider_time_from))
            : undefined,
          end_date: s.provider_date ? DateUtils.getShortDate(s.provider_date) : '',
          end_time: s.provider_time_to
            ? DateUtils.get24HourFormat(DateUtils.getTime(s.provider_time_to))
            : undefined,
          stop_type: (s.provider_stop_type?.id as STOP_TYPE) || undefined,
          notes: s.provider_include_location_notes ? s.notes : undefined,
          stop_identifier:
            s.provider_stop_type?.id && s.provider_stop_identifier
              ? s.provider_stop_identifier
              : undefined,
          contact: s.provider_include_location_notes ? s.contact : undefined,
          order_id: s.order_data?.order_id || undefined
        };

        if (s.provider_location?.id) {
          stop.location_id = s.provider_location.id;
        }

        return stop;
      });

      createTrip(tripRequest)
        .unwrap()
        .then(async (newTrip) => {
          if (tripData.documents && tripData.documents?.length > 0) {
            const docsWithOrder = tripData.documents.map((d) => {
              return { ...d, tripId: newTrip.id };
            });
            await Promise.all(
              docsWithOrder.map((doc) =>
                uploadOrderTripDocument({ document: doc, reviewed: true }).unwrap()
              )
            )
              .then(() => {
                resolve({ trip: newTrip, hasUploadDocumentsError: false });
              })
              .catch((error) => {
                logRocketTrack('Upload', {
                  action: 'Update document on create trip',
                  severity: 'high',
                  tenant_id: userData?.tenant?.id,
                  company_name: userData?.tenant?.company_name,
                  email: userData?.email_address,
                  user_first_name: userData?.first_name,
                  user_last_name: userData?.last_name
                });
                resolve({
                  trip: newTrip,
                  hasUploadDocumentsError: true
                });
              });
          } else {
            resolve({ trip: newTrip, hasUploadDocumentsError: false });
          }
        })
        .catch((error) => {
          reject(error.data.message);
        });
    });
  };

  const handleSubmit = async (ignoreAndProceed = false, fromWrapper = false): Promise<void> => {
    setIsSubmitting(true);

    // dispatch helper validation

    let validationsResponse: ValidationsResponse | null = null;
    let hasValidations = false;
    if (enableWarningHelper && !ignoreAndProceed) {
      try {
        const stops = tripData.provider_trip_stops;
        const tripToValidate = stops[0];
        const lastTrip = stops[stops.length - 1];

        validationsResponse = await validateDispatch({
          data: {
            flow: FLOWS.TRIPS,
            order_id: tripId,
            trips: [
              {
                id: tripToValidate.id ? tripToValidate.id : undefined,
                drivers: tripData.selected_drivers
                  .filter((d) => d.id)
                  .map((d) => {
                    return { id: d.id } as TripDriverInfo;
                  }),
                truck_id: tripData.selected_truck || undefined,
                trailer_id: tripData.selected_trailer || undefined,
                start_date: tripToValidate.provider_date
                  ? DateUtils.getShortDate(tripToValidate.provider_date)
                  : undefined,
                stop_date: lastTrip.provider_date
                  ? DateUtils.getShortDate(lastTrip.provider_date)
                  : undefined,
                start_from_time: tripToValidate.provider_time_from
                  ? DateUtils.get24HourFormat(DateUtils.getTime(tripToValidate.provider_time_from))
                  : undefined,
                stop_from_time: lastTrip.provider_time_to
                  ? DateUtils.get24HourFormat(DateUtils.getTime(lastTrip.provider_time_to))
                  : lastTrip.provider_time_from
                  ? DateUtils.get24HourFormat(DateUtils.getTime(lastTrip.provider_time_from))
                  : undefined
              }
            ]
          }
        }).unwrap();
      } catch (error) {
        console.error(error);
      }
    }

    if (validationsResponse) {
      hasValidations = validationsResponse.data.length > 0;

      // check if there are new validations
      const hasNewValidations = !_.isEqual(validationsResponse.data, validations);

      if (!hasNewValidations && !fromWrapper) {
        handleIgnoreAndProceed();

        return;
      }

      if (!hasValidations && !fromWrapper) {
        handleIgnoreAndProceed();

        return;
      }

      setOpenWarningHelper(hasValidations);
      setValidations(validationsResponse.data);
    }

    if (!hasValidations || ignoreAndProceed || !enableWarningHelper) {
      createTripAndUploadDocs()
        .then(({ trip: newTrip, hasUploadDocumentsError }) => {
          const notificationMessage = hasUploadDocumentsError
            ? 'Trip created, but documents failed to upload. Please re-upload.'
            : 'Trip created successfully.';
          setTripId(newTrip.id);
          const driversDifference = DriverUtils.findDifferenceOfDriversToAssign([], [newTrip]);
          // Returns driver's that are new or changed, in this case all are new
          if (!enableSkipDispatchModal && driversDifference.length > 0) {
            setDriversToNotify(driversDifference);
            dispatch(updateTripId(newTrip.id));
            setOpenNotify(true);
          } else {
            // close dialogs and open detail
            handleCloseCreateTrip();
          }

          // show notification for consolidation success
          addNotification({
            message: notificationMessage,
            variant: hasUploadDocumentsError ? 'warning' : 'success'
          });
          onConsolidationSuccess && onConsolidationSuccess(newTrip.id);
          onCreateSuccess && onCreateSuccess(newTrip.id);
        })
        .catch((err) => {
          addNotification({
            message: err.message || 'Error creating trip, please try again',
            variant: 'error'
          });
          logRocketTrack('Trip build', {
            action: err,
            severity: 'medium',
            tenant_id: userData?.tenant?.id,
            company_name: userData?.tenant?.company_name,
            email: userData?.email_address,
            user_first_name: userData?.first_name,
            user_last_name: userData?.last_name
          });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  const handleIgnoreAndProceed = (): void => {
    setOpenWarningHelper(false);
    handleSubmit(true);
  };

  const handleCloseWarningHelper = (): void => {
    setIsSubmitting(false);
    setOpenWarningHelper(false);
  };

  return (
    <>
      <TwDialog
        open={open}
        onClickClose={(): void => {
          setIsOpenWarning(true);
          dispatch(clearLoadState());
        }}
        fullScreen
        title="New trip"
        actions={[
          <TwButton
            key="1"
            type={TwButtonType.secondary}
            label="Cancel"
            action={(): void => {
              setIsOpenWarning(true);
            }}
          />,
          <TwButton
            key="2"
            type={TwButtonType.primary}
            label="Create"
            disabled={isSubmitting}
            action={handleFormSubmit}
          />
        ]}
      >
        <Grid container spacing={6}>
          {matchesAboveLarge && (
            <QuickMenuItems
              isOrderInfoVisible={isOrderInfoVisible}
              isRouteInfoVisible={isRouteInfoVisible}
              isDispatchInfoVisible={isDispatchInfoVisible}
              isAttachmentsVisible={isAttachmentsVisible}
              tripsVisibilities={[false]}
              orderInfoRef={orderInfoRef}
              routeInfoRef={routeInfoRef}
              dispatchRef={dispatchRef}
              attachmentsRef={attachmentsInfoRef}
              tripsRef={null}
              entity={EntityType.TRIP}
            />
          )}
          <Grid item xs={12} sm={12} md={12} lg={8} xl={8}>
            <TripWrapperForm
              onSubmit={(): Promise<void> => handleSubmit(false, true)}
              formRef={formRef}
            >
              <TripOrderInfo
                ref={orderInfoRef}
                ordersToConsolidate={consolidatedOrders}
                dataContainerSx={{
                  width: '100% !important',
                  maxWidth: `${858 / 16}rem`,
                  marginBottom: '3rem'
                }}
              />
              <TripRouteInfo
                ref={routeInfoRef}
                routeContainerSx={{
                  width: '100% !important',
                  marginTop: '3rem',
                  maxWidth: `${858 / 16}rem`
                }}
                className="create-trip__route"
              />
              <DispatchInfo
                ref={dispatchRef}
                dispatchContainerSx={{
                  width: '100% !important',
                  marginBottom: '3rem',
                  marginTop: '3rem',
                  maxWidth: `${858 / 16}rem`
                }}
                entity={EntityType.TRIP}
              />
              <AttachmentsInfo
                ref={attachmentsInfoRef}
                additionalContainerSx={{
                  width: '100% !important',
                  maxWidth: `${858 / 16}rem`,
                  marginBottom: '3rem'
                }}
                entity={EntityType.TRIP}
              />
            </TripWrapperForm>
          </Grid>
          {matchesAboveLarge && <Grid item lg={2} xl={2}></Grid>}
        </Grid>
      </TwDialog>
      <TwLeaveWarning
        isOpenWarning={isOpenWarning}
        closeWarning={handleWarningClose}
        title={'Leave page?'}
        warningMessage={
          <>
            You haven’t finished creating a trip.
            <br /> Do you want to leave without finishing?
          </>
        }
        handleClose={handleCloseCreateTrip}
        saveAndContinue={handleWarningClose}
      />
      <TwNotifyDriver
        open={openNotify}
        driversToNotify={driversToNotify}
        unassignedDriversToNotify={[]}
        onClose={closeNotify}
        isSplitLoad={false}
        isSegmentShare={true}
        isForNewLoad={true}
        isNormalShare={false}
        tripId={tripId}
      />
      {driver_pay_confirmation_modal.open && (
        <TwDriverPayConfirmationModal open={driver_pay_confirmation_modal.open} />
      )}

      {enableWarningHelper && (
        <TwWarningHelper
          validations={validations}
          open={openWarningHelper}
          handleClose={handleCloseWarningHelper}
          onContinue={handleIgnoreAndProceed}
        />
      )}
    </>
  );
};

export default CreateTrip;
